import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  ACTION_NAME_PAUSE_SHIFT,
  ACTION_NAME_START_SHIFT,
  ACTION_NAME_UNPAUSE_SHIFT,
  SHIFT_STATUS_ONGOING,
  SHIFT_STATUS_PAUSED,
  SHIFT_STATUS_VALIDATE,
} from '../../../constants.ts';
import { SelectedVehicleContext } from '../../../contexts/vehicle/SelectedVehicleContext.ts';
import { postActions } from '../../../queries/postActions.ts';
import { Action } from '../../../schema/Action.ts';
import { IShiftAction } from '../../../schema/ShiftActionPayload.ts';
import { useShiftByVehicle } from '../../../services/Vehicle.ts';
import { CustomSpinner } from '../../ui/Spinner.tsx';

export function ShiftActions() {
  const { selectedVehicle } = useContext(SelectedVehicleContext);
  const params = useParams() as { territorySlug: string };
  const { data: shiftData } = useShiftByVehicle(params.territorySlug, selectedVehicle?.id);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mapping = {
    ACTION_NAME_START_SHIFT: {
      successMessage: t('roadmap.start-shift'),
      errorMessage: t('roadmap.start-shift-error'),
      payload: { name: ACTION_NAME_START_SHIFT, kwargs: {} },
    },
    ACTION_NAME_PAUSE_SHIFT: {
      successMessage: t('vehicle.pause-shift'),
      errorMessage: t('vehicle.pause-shift-error'),
      payload: { name: ACTION_NAME_PAUSE_SHIFT, kwargs: {} },
    },
    ACTION_NAME_UNPAUSE_SHIFT: {
      successMessage: t('vehicle.unpause-shift'),
      errorMessage: t('vehicle.unpause-shift-error'),
      payload: { name: ACTION_NAME_UNPAUSE_SHIFT, kwargs: {} },
    },
  };

  const mutation = useMutation({
    mutationFn: (payload: Action) =>
      postActions(params.territorySlug, selectedVehicle?.id || 0, [payload]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift', selectedVehicle?.id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', params.territorySlug] });
    },
  });

  function performShiftAction(shiftAction: IShiftAction) {
    mutation.mutate(shiftAction.payload, {
      onSuccess: () => toast.success(shiftAction.successMessage),
      onError: () => toast.error(shiftAction.errorMessage),
    });
  }

  if (shiftData?.status === SHIFT_STATUS_VALIDATE) {
    return (
      <div className={'col-2 my-auto text-center'}>
        <Button
          onClick={() => performShiftAction(mapping.ACTION_NAME_START_SHIFT)}
          className={'primary-button small-button'}>
          {mutation.isPending ? (
            <CustomSpinner class={'d-flex justify-content-center'} size={'sm'} />
          ) : (
            <span>
              <img src={'/theme/media/unpause.svg'} width="25" height="25" alt="Start shift icon" />
            </span>
          )}
        </Button>
      </div>
    );
  } else if (shiftData?.status === SHIFT_STATUS_ONGOING) {
    return (
      <div className={'col-2 my-auto text-center'}>
        <Button
          onClick={() => performShiftAction(mapping.ACTION_NAME_PAUSE_SHIFT)}
          className={'alert-button small-button'}>
          {mutation.isPending ? (
            <CustomSpinner class={'d-flex justify-content-center'} size={'sm'} />
          ) : (
            <span>
              <img src={'/theme/media/pause.svg'} width="25" height="25" alt="Pause icon" />
            </span>
          )}
        </Button>
      </div>
    );
  } else if (shiftData?.status === SHIFT_STATUS_PAUSED) {
    return (
      <div className={'col-2 my-auto text-center'}>
        <Button
          onClick={() => performShiftAction(mapping.ACTION_NAME_UNPAUSE_SHIFT)}
          className={'primary-button small-button'}>
          {mutation.isPending ? (
            <CustomSpinner class={'d-flex justify-content-center'} size={'sm'} />
          ) : (
            <span>
              <img src={'/theme/media/unpause.svg'} width="25" height="25" alt="Un Pause icon" />
            </span>
          )}
        </Button>
      </div>
    );
  }
}
